import React from 'react';
import { Link } from 'react-router-dom';
import { FaFacebook, FaInstagram, FaEnvelope, FaPhone, FaDownload } from 'react-icons/fa';

const Footer = () => {
  const [showInstallModal, setShowInstallModal] = React.useState(false);
  const [canInstall, setCanInstall] = React.useState(false);
  const [isIOS] = React.useState(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
  });
  const deferredPromptRef = React.useRef(null);

  React.useEffect(() => {
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setCanInstall(true);
    };

    const onAppInstalled = () => {
      deferredPromptRef.current = null;
      setCanInstall(false);
      setShowInstallModal(false);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const openInstallModal = () => {
    if (!canInstall && !isIOS) return;
    setShowInstallModal(true);
  };

  const handleInstallClick = async () => {
    const promptEvent = deferredPromptRef.current;
    if (!promptEvent) return;

    promptEvent.prompt();
    const result = await promptEvent.userChoice;

    if (result.outcome === "accepted") {
      console.log("User installed the app");
    }
    setShowInstallModal(false);
    deferredPromptRef.current = null;
    setCanInstall(false);
  };

  return (
    <>
      {/* ✅ Install Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-[#18100d] text-cigar-cream p-6 rounded-xl shadow-xl max-w-sm w-full text-center border border-cigar-brown">
            <h2 className="text-xl font-bold mb-3">Install CC Cigar</h2>
            {isIOS ? (
              <>
                <p className="text-cigar-cream/80 mb-4">
                  To install CC Cigar on your iOS device:
                </p>
                <video
                  className="w-full mb-4 rounded-lg"
                  src="/1028.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  controls
                >
                  Your browser doesn't support video playback.
                </video>
                <ol className="text-left text-sm text-cigar-cream/80 mb-6 space-y-2">
                  <li>1. Tap the share button <span className="inline-block">⎙</span></li>
                  <li>2. Scroll down and tap "Add to Home Screen"</li>
                  <li>3. Tap "Add" to confirm</li>
                </ol>
              </>
            ) : (
              <>
                <p className="text-cigar-cream/80 mb-6">
                  Add CC Cigar to your home screen for a faster shopping experience.
                </p>
                <div className="flex items-center justify-center gap-3">
                  <button
                    className="px-4 py-2 bg-cigar-gold text-black rounded-lg font-semibold"
                    onClick={handleInstallClick}
                  >
                    Install App
                  </button>
                </div>
              </>
            )}
            <button
              className="px-4 py-2 border border-cigar-cream/60 rounded-lg text-cigar-cream/70"
              onClick={() => setShowInstallModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* ✅ Footer */}
      <footer className="bg-[#18100d] text-cigar-cream border-t border-cigar-brown/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-cigar-gold rounded-sm flex items-center justify-center shadow">
                  <span className="text-[#18100d] font-bold text-lg">CC</span>
                </div>
                <span className="text-xl font-cigar font-semibold">CC Cigar</span>
              </div>
              <p className="text-cigar-cream/80 mb-4 max-w-md">
                Premium cigars for the discerning connoisseur. We bring you the finest
                handcrafted cigars from around the world, carefully selected for quality and taste.
              </p>
              <div className="flex space-x-4">
                <a
                  href="https://www.facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cigar-gold hover:text-cigar-cream transition-colors"
                >
                  <FaFacebook size={20} />
                </a>
                <a
                  href="https://www.instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cigar-gold hover:text-cigar-cream transition-colors"
                >
                  <FaInstagram size={20} />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/" className="text-cigar-cream/80 hover:text-cigar-gold">Home</Link></li>
                <li><Link to="/products" className="text-cigar-cream/80 hover:text-cigar-gold">Cigars</Link></li>
                <li><Link to="/about" className="text-cigar-cream/80 hover:text-cigar-gold">About Us</Link></li>
                <li><Link to="/contact" className="text-cigar-cream/80 hover:text-cigar-gold">Contact</Link></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <FaEnvelope className="text-cigar-gold" />
                  <span className="text-cigar-cream/80">CCcigar.eg@gmail.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FaPhone className="text-cigar-gold" />
                  <span className="text-cigar-cream/80">01200111567</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Area */}
          <div className="border-t border-cigar-brown/40 mt-8 pt-8 text-center">
            <div className="flex items-center justify-center gap-4">
              <p className="text-cigar-cream/70">© 2024 CC Cigar. All rights reserved.</p>

              {(canInstall || isIOS) && (
                <button
                  onClick={openInstallModal}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-cigar-gold to-cigar-bronze text-cigar-dark font-semibold rounded-lg shadow-sm hover:opacity-90 transition-all duration-300"
                >
                  <FaDownload className="animate-bounce" />
                  <span className="text-sm">Install App</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
